import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useCallback, useState} from "react";
import {createBookingTimeSlotsRepository} from "../data/repositories";
import {BookingTimeSlots, BookingTimeSlotsQuery} from "../domain/types";

export const BOOKING_TIME_SLOTS_QUERY_KEY = "booking_time_slots";

type ApiError = AxiosError<{status: boolean; message: string}>;

export type UseBookingTimeSlotsResult = {
  data: BookingTimeSlots | undefined;
  /** True only while the selected date has no slots cached yet. */
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: ApiError | null;
  /** Loads the slots for a date. Safe to call repeatedly; results are cached. */
  fetchTimeSlots: (query: BookingTimeSlotsQuery) => void;
  /** Clears the selection, hiding the slot list. */
  clearTimeSlots: () => void;
};

/**
 * View-logic hook: loads the bookable times for whichever date the user picked
 * on the booking calendar.
 *
 * The calendar drives this imperatively (`fetchTimeSlots` on date click), but the
 * result is held in the query cache keyed by the date, so re-picking a date the
 * user already visited renders from cache and revalidates in the background
 * instead of blanking the list.
 */
export const useBookingTimeSlots = (): UseBookingTimeSlotsResult => {
  const repository = createBookingTimeSlotsRepository();
  const [query, setQuery] = useState<BookingTimeSlotsQuery | null>(null);

  const result = useQuery<BookingTimeSlots, ApiError>({
    queryKey: [
      BOOKING_TIME_SLOTS_QUERY_KEY,
      query?.mentorId,
      query?.serviceId,
      query?.year,
      query?.month,
      query?.date,
      query?.userTimeZone,
    ],
    queryFn: () => repository.getTimeSlots(query as BookingTimeSlotsQuery),
    enabled: query !== null,
    // Slots go stale as other customers book, so always revalidate on reselect.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const clearTimeSlots = useCallback(() => setQuery(null), []);

  return {
    data: result.data,
    isLoading: result.isInitialLoading,
    isSuccess: result.isSuccess,
    isError: result.isError,
    error: result.error ?? null,
    fetchTimeSlots: setQuery,
    clearTimeSlots,
  };
};
